import { useCallback, useMemo } from 'react';

import { buildPropertyPanelTitle, coerceSelfLoopEdgeType, type FlowEdgeLike } from './flowEdgeHelpers';
import { clampLoopEdgeRestartPatch, parseEdgeDataObject, type LoopEdgeRestartData } from './loopEdgeRestartData';

interface FlowEditorState {
  readonly child_edges?: readonly FlowEdgeLike[];
}

type SetState<S extends FlowEditorState> = (updater: S | ((prev: S) => S)) => void;

interface Options<S extends FlowEditorState> {
  readonly setState: SetState<S>;
  readonly child_edges: readonly FlowEdgeLike[];
  /** The edge the panel shows, from the canvas selection (see `panelTarget`); null when it shows no edge. */
  readonly selectedEdgeId: string | null;
  /** The name of the node the panel shows, once its record has loaded. */
  readonly selectedNodeName: string | null | undefined;
}

export function useFlowEdgeSelection<S extends FlowEditorState>({ setState, child_edges, selectedEdgeId, selectedNodeName }: Options<S>) {
  const selectedEdge = useMemo(
    () => (selectedEdgeId ? ((child_edges || []).find((edge) => edge.id === selectedEdgeId) ?? null) : null),
    [selectedEdgeId, child_edges]
  );

  const panelTitle = useMemo(() => buildPropertyPanelTitle(selectedEdge, selectedNodeName), [selectedEdge, selectedNodeName]);

  const handleLoopEdgeRestartChange = useCallback(
    (edgeId: string, next: LoopEdgeRestartData) => {
      const clamped = clampLoopEdgeRestartPatch(next);
      setState((prev) => ({
        ...prev,
        child_edges: (prev.child_edges || []).map((edge) => {
          if (edge.id !== edgeId) {
            return edge;
          }
          return {
            ...edge,
            type: coerceSelfLoopEdgeType(edge),
            data: {
              ...parseEdgeDataObject(edge.data),
              ...clamped,
            },
          };
        }),
      }));
    },
    [setState]
  );

  return {
    selectedEdge,
    panelTitle,
    handleLoopEdgeRestartChange,
  };
}
